import { runCordovaPluginCall, runCordovaPluginPromise } from '@helpers/cordovaBridge';

// Meta's iOS SDK (v17+) reads the ATT status itself on iOS 17 and later. On iOS 15-16 it
// instead uses the advertiser-tracking flag the app last set, so mirror the ATT answer there.
// Read-only: this never shows the ATT prompt; private.router.tsx owns that flow.

interface IIdfaInfo {
  trackingPermission?: number;
}

interface IIdfaPlugin {
  TRACKING_PERMISSION_NOT_DETERMINED: number;
  TRACKING_PERMISSION_AUTHORIZED: number;
  getInfo: () => Promise<IIdfaInfo>;
}

interface IFacebookConnectPlugin {
  setAdvertiserTrackingEnabled: (enabled: boolean, success?: () => void, failure?: (error: any) => void) => void;
}

const FIRST_IOS_WHERE_SDK_READS_ATT = 17;

function sdkReadsAttItself(): boolean {
  const version = (window as any).device?.version;
  const major = typeof version === 'string' ? parseInt(version, 10) : NaN;
  return !isNaN(major) && major >= FIRST_IOS_WHERE_SDK_READS_ATT;
}

export function syncMetaAdvertiserTracking(): Promise<boolean> {
  if (typeof window === 'undefined' || (window as any).cordova?.platformId !== 'ios' || sdkReadsAttItself()) {
    return Promise.resolve(false);
  }
  const idfa: IIdfaPlugin | undefined = (window as any).cordova.plugins?.idfa;
  const facebook: IFacebookConnectPlugin | undefined = (window as any).facebookConnectPlugin;
  if (!idfa?.getInfo || !facebook?.setAdvertiserTrackingEnabled) {
    return Promise.resolve(false);
  }

  // Start inside the chain so a synchronous native error is caught as well.
  return Promise.resolve()
    .then(() => runCordovaPluginPromise<IIdfaInfo>('Idfa.getInfo for Meta', () => idfa.getInfo()))
    .then((info) => {
      const permission = info?.trackingPermission;
      if (typeof permission !== 'number' || permission === idfa.TRACKING_PERMISSION_NOT_DETERMINED) {
        // Not answered yet: keep the SDK's default rather than reporting an opt-out.
        return false;
      }
      const enabled = permission === idfa.TRACKING_PERMISSION_AUTHORIZED;
      return runCordovaPluginCall('facebookConnectPlugin.setAdvertiserTrackingEnabled', () => {
        facebook.setAdvertiserTrackingEnabled(enabled);
        return true;
      }, false) === true;
    })
    // Attribution must never break the app.
    .catch(() => false);
}

export function installMetaAdvertiserTrackingSync(): void {
  if ((window as any).cordova?.platformId !== 'ios') {
    return;
  }
  void syncMetaAdvertiserTracking();
  // iOS fires 'active' again when the ATT alert closes; 'resume' does not.
  document.addEventListener('active', () => { void syncMetaAdvertiserTracking(); }, false);
}



// WEBPACK FOOTER //
// ./src/services/meta/metaAdvertiserTracking.ts